<script setup>
import ReplicateEmailingAction from '@frontJs/components/actions/projectManagement/modules/ReplicateEmailingAction.vue';
import SendEmailingTestEmailAction from '@frontJs/components/actions/projectManagement/modules/SendEmailingTestEmailAction.vue';
import CopyIcon from '@iconify-vue/ph/copy';
import EnvelopeIcon from '@iconify-vue/ph/envelope';
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import WarningIcon from '@iconify-vue/ph/warning-fill';
import DisplayHtmlPreviewAction from '@js/components/actions/DisplayHtmlPreviewAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, all = false } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    all: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenant.name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Client',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'subject',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Objet',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.subject,
                }),
        }),
        columnHelper.display({
            id: 'template',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Template',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            props.row.original.htmlPreview
                                ? h(
                                      DisplayHtmlPreviewAction,
                                      {
                                          title: 'Template',
                                          html: props.row.original.htmlPreview,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  title: 'Voir le template',
                                                  type: 'primary',
                                                  square: true,
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : h(WarningIcon, {
                                      class: 'size-5 text-red-500',
                                  }),
                            !props.row.original.hasUnsubscriptionUrl
                                ? h(
                                      'span',
                                      {
                                          class: 'inline-flex',
                                          title: 'La variable {{ $unsubscription_url }} est absente du template.',
                                      },
                                      h(WarningIcon, {
                                          class: 'size-5 text-yellow-500',
                                      }),
                                  )
                                : null,
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'isActive',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actif',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isActive,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'date',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: "Date d'envoi",
                }),
            cell: (props) =>
                props.row.original.date
                    ? h(TextCell, {
                          text: props.row.original.date,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfEmailsThatCanBeSent',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB emails<br/>pouvant être envoyés',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfEmailsThatCanBeSent,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfPendingEmailingEmails',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB emails<br/>en attente',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfPendingEmailingEmails,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfSentEmailingEmails',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB emails<br/>envoyés',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfSentEmailingEmails,
                }),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.modules.emailing',
                                    {
                                        tenant: tenant
                                            ? tenant.id
                                            : props.row.original.tenant.id,
                                        emailing: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                            tenant
                                ? h(
                                      ReplicateEmailingAction,
                                      {
                                          tenant,
                                          emailing: props.row.original,
                                          callback: () => {
                                              updateData();
                                          },
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  title: 'Répliquer',
                                                  type: 'secondary',
                                                  square: true,
                                                  icon: CopyIcon,
                                              }),
                                      },
                                  )
                                : null,
                            h(
                                SendEmailingTestEmailAction,
                                {
                                    tenant: tenant
                                        ? tenant
                                        : props.row.original.tenant,
                                    emailing: props.row.original,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            title: 'Envoyer un email de test',
                                            type: 'secondary',
                                            square: true,
                                            icon: EnvelopeIcon,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.modules.postEmailings', {
        tenant: tenant ? tenant.id : null,
        all,
    }),
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        'tenant.name': !tenant,
        id: !!tenant,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                v-if="tenant"
                size="small"
                :href="
                    route('front.projectManagement.modules.emailing', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
